import { useState, useMemo, useEffect } from 'react';
import { OrderFilterConfig } from './OrderFieldFilter';
import { OrderNotification } from '@shared/contexts/OrderNotificationsContexts';

interface FilterState {
  [key: string]: string[];
}

interface UseOrderFiltersProps {
  notifications: OrderNotification[];
  filterConfigs: OrderFilterConfig[];
}

export const useOrderFilters = ({ notifications, filterConfigs }: UseOrderFiltersProps) => {
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({});

  useEffect(() => {
    if (filterConfigs.some(config => config.options.length > 0)) {
      filterConfigs.forEach(config => {
        setSelectedFilters(prev => {
          const newFilters = { ...prev };
          newFilters[config.field] = config.options.map(o => o.value);
          return newFilters;
        });
      });
    }
  }, [filterConfigs]);

  const handleFilterChange = (field: string, value: string) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev };
      if (newFilters[field]?.includes(value)) {
        newFilters[field] = newFilters[field]?.filter(v => v !== value);
      } else {
        newFilters[field] = [...(newFilters[field] || []), value];
      }
      return newFilters;
    });
  };

  const handleSelectAll = (field: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [field]:
        prev[field]?.length === filterConfigs.find(config => config.field === field)?.options.length
          ? []
          : filterConfigs.find(config => config.field === field)?.options.map(o => o.value) || [],
    }));
  };

  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      const order = notification.order;

      return filterConfigs.every(config => {
        const field = config.field;
        const selectedValues = selectedFilters[field] || [];
        const orderValue = config.getValue(order);
        return selectedValues.includes(orderValue);
      });
    });
  }, [notifications, selectedFilters, filterConfigs]);

  return {
    filteredNotifications,
    handleFilterChange,
    handleSelectAll,
    selectedFilters,
  };
};
